import { useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Power, KeyRound, Send, RefreshCcw } from "lucide-react";

/**
 * INCIDENT simulation. Used by incident response concepts.
 * Events: alert_inspected, incident_recognized, network_disconnected,
 * account_locked, password_changed, incident_reported, public_disclosure,
 * incident_unreported, recovery_action, unsafe_recovery_action,
 * wrong_alert_flagged, final_decision.
 */
export default function IncidentSim({ scenario, recorder, onFinalize, busy }) {
  const alerts = scenario.alerts || [];
  const channels = scenario.channels || [];
  const recovery = scenario.recovery_options || [];
  const [recognised, setRecognised] = useState(null);
  const [disconnected, setDisconnected] = useState(false);
  const [locked, setLocked] = useState(false);
  const [pwChanged, setPwChanged] = useState(false);
  const [reported, setReported] = useState(null);
  const [recoveryPicks, setRecoveryPicks] = useState([]);

  const inspect = (a) => recorder.log("alert_inspected", a.id);
  const recognise = (a) => {
    setRecognised(a);
    if (a.indicator) recorder.log("incident_recognized", a.id);
    else recorder.log("wrong_alert_flagged", a.id);
  };
  const toggleRecovery = (r) => {
    const present = recoveryPicks.includes(r.id);
    setRecoveryPicks(present ? recoveryPicks.filter((x) => x !== r.id) : [...recoveryPicks, r.id]);
    recorder.log("recovery_action", r.id);
    if (r.unsafe) recorder.log("unsafe_recovery_action", r.id);
  };

  const disconnect = () => { setDisconnected(true); recorder.log("network_disconnected"); };
  const lock = () => { setLocked(true); recorder.log("account_locked"); };
  const changePw = () => { setPwChanged(true); recorder.log("password_changed"); };
  const report = (c) => {
    setReported(c);
    if (c.unsafe) recorder.log("public_disclosure", c.id);
    else recorder.log("incident_reported", c.id);
  };

  const finalize = () => {
    if (!reported) recorder.log("incident_unreported");
    let decision = "no_action";
    if (recovery.length) decision = recoveryPicks.length > 0 && !recoveryPicks.some((id) => recovery.find((r) => r.id === id)?.unsafe) ? "recovered" : "incomplete_recovery";
    else if (channels.length === 1 && reported) decision = `reported:${reported.id}`;
    else if (recognised) decision = `recognised:${recognised.id}`;
    if (disconnected && pwChanged) decision = "contained";
    onFinalize(decision, { recognised, disconnected, locked, pwChanged, reported, recoveryPicks });
  };

  return (
    <div className="grid lg:grid-cols-12 min-h-[460px]" data-testid="incident-sim">
      <div className="lg:col-span-5 p-5 border-b lg:border-b-0 lg:border-r border-[var(--iddl-rule)]">
        <div className="label-mono flex items-center gap-2"><AlertTriangle size={12} /> Alerts</div>
        <ul className="mt-2 grid gap-2">
          {alerts.map((a) => (
            <li key={a.id} className={`paper border p-3 ${recognised?.id === a.id ? "border-[var(--iddl-ink)]" : "border-[var(--iddl-rule)]"}`} data-testid={`alert-${a.id}`}>
              <div className="text-sm">{a.label}</div>
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => inspect(a)} data-testid={`btn-alert-inspect-${a.id}`}>Inspect</Button>
                <Button size="sm" className="btn-ink rounded-none" onClick={() => recognise(a)} data-testid={`btn-alert-recognise-${a.id}`}>Flag as incident</Button>
              </div>
            </li>
          ))}
        </ul>

        {recovery.length > 0 && (
          <div className="mt-5">
            <div className="label-mono flex items-center gap-2"><RefreshCcw size={12} /> Recovery</div>
            <ul className="mt-2 grid gap-1">
              {recovery.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => toggleRecovery(r)}
                    data-testid={`recovery-${r.id}`}
                    className={`w-full text-left p-2 border ${recoveryPicks.includes(r.id) ? "border-[var(--iddl-ink)] bg-[var(--iddl-surface)]" : "border-[var(--iddl-rule)]"}`}
                  >
                    <span className="text-sm">{r.label}</span>
                    {r.unsafe && <span className="label-mono tint-cs border px-1.5 py-0.5 ml-2">UNSAFE</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="lg:col-span-7 p-5">
        <div className="label-mono flex items-center gap-2"><Power size={12} /> Containment</div>
        <div className="mt-2 grid gap-2 grid-cols-2 sm:grid-cols-3">
          <Button variant="outline" className={`rounded-none ${disconnected ? "bg-[var(--iddl-surface)]" : ""}`} onClick={disconnect} data-testid="btn-disconnect">
            <Power size={12} className="mr-1" /> {disconnected ? "Disconnected" : "Disconnect"}
          </Button>
          <Button variant="outline" className={`rounded-none ${locked ? "bg-[var(--iddl-surface)]" : ""}`} onClick={lock} data-testid="btn-lock-account">
            <KeyRound size={12} className="mr-1" /> {locked ? "Locked" : "Lock account"}
          </Button>
          <Button variant="outline" className={`rounded-none ${pwChanged ? "bg-[var(--iddl-surface)]" : ""}`} onClick={changePw} data-testid="btn-change-password">
            <KeyRound size={12} className="mr-1" /> {pwChanged ? "Password rotated" : "Rotate password"}
          </Button>
        </div>

        {channels.length > 0 && (
          <>
            <div className="label-mono flex items-center gap-2 mt-5"><Send size={12} /> Reporting channel</div>
            <ul className="mt-2 grid gap-2">
              {channels.map((c) => (
                <li key={c.id}>
                  <Button
                    variant="outline"
                    className={`rounded-none ${reported?.id === c.id ? "bg-[var(--iddl-surface)]" : ""} ${c.valid ? "" : "tint-cs"}`}
                    onClick={() => report(c)}
                    data-testid={`btn-report-${c.id}`}
                  >
                    {c.label}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        )}

        <div className="mt-6 pt-4 border-t border-[var(--iddl-rule)] flex justify-end">
          <Button onClick={finalize} disabled={busy} className="btn-ink rounded-none" data-testid="btn-finalize-incident">Finalise — close incident response</Button>
        </div>
      </div>
    </div>
  );
}
